import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Calendar, Award, BookOpen } from 'lucide-react';

const Education = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="education" ref={sectionRef} className="py-24 md:py-32 bg-[#0f1d32]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`mb-16 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="text-[#d4af37] text-sm font-semibold uppercase tracking-[0.2em]">Academic Background</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4">
            Education
          </h2>
          <div className="w-20 h-1 bg-[#d4af37] mt-6" />
        </div>

        {/* Main Education Cards */}
        <div className={`grid md:grid-cols-2 gap-6 mb-8 ${isVisible ? 'animate-fade-up delay-200' : 'opacity-0'}`}>
          {data.slice(0, 2).map((edu, index) => (
            <div
              key={edu.id}
              className="group bg-[#1a2744] p-8 border border-[#d4af37]/10 hover:border-[#d4af37]/40 transition-all duration-500 hover:-translate-y-1"
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#d4af37] transition-colors duration-300">
                  <GraduationCap size={32} className="text-[#d4af37] group-hover:text-[#0a1628] transition-colors" />
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-white group-hover:text-[#d4af37] transition-colors">
                    {edu.institution}
                  </h3>
                  <div className="flex items-center gap-2 text-[#d4af37]">
                    <BookOpen size={16} />
                    <span className="font-medium">{edu.degree}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748b]">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={14} />
                      <span>{edu.duration}</span>
                    </div>
                    {edu.grade && (
                      <div className="flex items-center gap-1.5">
                        <Award size={14} className="text-[#d4af37]" />
                        <span className="text-[#d4af37] font-semibold">{edu.grade}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-[#94a3b8] text-sm">{edu.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary Education - Compact */}
        {data.length > 2 && (
          <div className={`grid md:grid-cols-2 gap-4 ${isVisible ? 'animate-fade-up delay-400' : 'opacity-0'}`}>
            {data.slice(2).map((edu) => (
              <div
                key={edu.id}
                className="bg-[#1a2744]/50 border border-[#d4af37]/10 p-5 flex items-center gap-4 hover:border-[#d4af37]/30 transition-all"
              >
                <div className="w-10 h-10 bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={20} className="text-[#d4af37]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">{edu.degree}</h4>
                  <p className="text-sm text-[#64748b]">
                    {edu.institution} • {edu.duration} • <span className="text-[#d4af37]">{edu.grade}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Education;
